import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { ProgressIndicator, Text } from '@oneaudi/unified-web-components';
import { useI18n } from '@oneaudi/i18n-context';
import mapContext from '../../../contexts/map-context';
import { DealerReview, RatingSummary, RatingText, StarFilter } from '../../../types';
import { ActionTypes } from '../../../contexts/reducers/map-reducer';
import { generateMercantileValue } from '../../../utils/generateMercantileValue';
import { messages } from '../../../i18n/messages';

enum RatingBarState {
  DEFAULT,
  SELECTED,
  UNSELECTED,
  DISABLED,
}

const RatingBreakdownContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-self: center;
  row-gap: 8px;
  width: min(100%, 338px);
  color: #333333;
  user-select: none;

  .bar div:first-child > div:first-child {
    display: none;
  }

  .star-text {
    width: max-content;
  }
`;

const RatingBar = styled.div<{ state: RatingBarState; starsWidth: string }>`
  align-items: center;
  display: grid;
  grid-template-columns: ${({ starsWidth }) => starsWidth} 1fr 37px;
  column-gap: 16px;

  ${(props) => {
    switch (props.state) {
      case RatingBarState.DEFAULT:
        return `
          cursor: pointer;

          :hover, :focus {
            p {
              font-weight: 700;
              text-decoration: underline;
            }
          }
          `;
      case RatingBarState.DISABLED:
        return `
          cursor: not-allowed;

          p {
            color: #D9D9D9;
          }

          div {
            div {
              div {
                background: #FFFFFF;
                div {
                  background: #D9D9D9;
                }
              }
            }
          }
          `;
      case RatingBarState.SELECTED:
        return `
          cursor: pointer;

          p {
            font-weight: 700;
          }

          :hover, :focus {
            p {
              text-decoration: underline;
            }
          }
          `;
      case RatingBarState.UNSELECTED:
        return `
          cursor: pointer;
          
          p {
            color: #666666;
          }

          div {
            div {
              div {
                background: #FFFFFF;
                div {
                  background: #D9D9D9;
                }
              }
            }
          }

          :hover, :focus {
            p {
              text-decoration: underline;
            }
          }
          `;
      default:
        return '';
    }
  }}
`;

const RATINGS: [RatingText, StarFilter][] = [
  ['five', '5 star'],
  ['four', '4 star'],
  ['three', '3 star'],
  ['two', '2 star'],
  ['one', '1 star'],
];

type ReviewSummary = { [key: string]: boolean };

export const RatingBreakdown = () => {
  const { dealerReviews, ratingSummary, reviewStarFilter } = mapContext.useMapState();
  const [reviewSummary, setReviewSummary] = useState<ReviewSummary>({});
  const [starsWidth, setStarsWidth] = useState('auto');
  const containerRef = useRef(null);
  useEffect(() => {
    if (containerRef?.current) {
      const container = containerRef.current as HTMLElement;
      const firstRow = container.firstChild as HTMLElement;
      const starsText = firstRow.firstChild as HTMLElement;
      const newColumnWidth = `${Math.ceil(starsText.clientWidth * 1.027)}px`;
      setStarsWidth(newColumnWidth);
    }
  }, []);
  useEffect(() => {
    const newReviewSummary =
      dealerReviews?.reduce((summary: ReviewSummary, dealerReview: DealerReview) => {
        const rating = Math.floor(+generateMercantileValue(dealerReview.rating));
        const ratingItem = RATINGS.find((ratingGroup) => +ratingGroup[1][0] === rating);
        const starFilter = ratingItem ? ratingItem[1] : '';
        return { ...summary, [starFilter]: true };
      }, {}) || {};
    setReviewSummary(newReviewSummary);
  }, []);
  const dispatch = mapContext.useMapDispatch();
  if (!ratingSummary) {
    return <></>;
  }
  const onRatingBarSelect = (starFilter: StarFilter) => {
    if (reviewSummary[starFilter] && reviewStarFilter !== starFilter) {
      dispatch({ type: ActionTypes.setReviewStarFilter, payload: starFilter });
      dispatch({ type: ActionTypes.setReviewSort, payload: 'Newest' });
    } else if (reviewSummary[starFilter] && reviewStarFilter === starFilter) {
      dispatch({ type: ActionTypes.setReviewStarFilter, payload: 'All stars' });
      dispatch({ type: ActionTypes.setReviewSort, payload: 'Newest' });
    }
  };
  const onRatingBarKeyDown = (event: React.KeyboardEvent, starFilter: StarFilter) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onRatingBarSelect(starFilter);
    }
  };
  return (
    <RatingBreakdownContainer ref={containerRef}>
      {RATINGS.map(([ratingText, starFilter]) => {
        const ratingValue = ratingSummary[ratingText as keyof Omit<RatingSummary, 'totalRatings'>];
        const percentage = Math.round(((ratingValue || 0) / ratingSummary.totalRatings) * 100);
        const state = (() => {
          if (!Object.keys(reviewSummary)) {
            return RatingBarState.DEFAULT;
          }
          if (reviewSummary[starFilter] && starFilter === reviewStarFilter) {
            return RatingBarState.SELECTED;
          }
          if (reviewSummary[starFilter] && reviewStarFilter !== 'All stars') {
            return RatingBarState.UNSELECTED;
          }
          if (!reviewSummary[starFilter]) {
            return RatingBarState.DISABLED;
          }
          return RatingBarState.DEFAULT;
        })();
        const starText =
          starFilter[0] === '1'
            ? useI18n(messages.five_star_single_star)
            : useI18n(messages.five_star_plural_star);
        return (
          <RatingBar
            tabIndex={state === RatingBarState.DISABLED ? -1 : 0}
            state={state}
            starsWidth={starsWidth}
            key={ratingText}
            onClick={() => onRatingBarSelect(starFilter)}
            onKeyDown={(event) => onRatingBarKeyDown(event, starFilter)}
          >
            <Text
              className="star-text"
              tag="p"
              variant={{ order: '2', style: 'normal', type: 'copy' }}
            >{`${starFilter[0]} ${starText}`}</Text>
            <ProgressIndicator
              variant="bar"
              progress={percentage}
              colorScheme="idle"
              type="finite"
              className="bar"
            />
            <Text variant={{ order: '2', style: 'normal', type: 'copy' }}>{`${percentage}%`}</Text>
          </RatingBar>
        );
      })}
    </RatingBreakdownContainer>
  );
};
